// src/components/FormulesRepartitions/RepartitionPouleTrois.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import CombatModal from "./../CombatModal";
import EditCombatModal from "./../EditCombatModal";
import { getDistributionCombats } from "../../services/combatsServices";
import { calculateClassementPoule } from "../../utils/classementPouleUtils";
import { updateCompetiteurResults } from "../../services/competiteursServices";
import { finalizeDistribution } from "../../services/distributionsServices";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import CombatDetailsModal from "./components/CombatDetailsModal";

/* ------------------------------ Helpers ------------------------------ */

// essaie de lire une "position" provenant de différentes sources possibles.
// on garde une large compatibilité avec ce que renvoie le back/jointure.
const readPos = (c, fallbackIndex) => {
  const candidates = [
    c._pos,
    c.position,
    c.position_aleatoire,
    c.poule_position,
    c.distribution_position,
    c?.DistributionCompetiteur?.position,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return fallbackIndex + 1; // sinon on garde l’ordre d’entrée
};

// format badge résultat
const resultColor = (r) => {
  switch (r) {
    case "V":
      return "bg-emerald-200 text-emerald-900 dark:bg-emerald-200 dark:text-emerald-200";
    case "D":
      return "bg-rose-200 text-rose-900 dark:bg-rose-200 dark:text-rose-200";
    case "N":
      return "bg-amber-200 text-amber-900 dark:bg-amber-200 dark:text-amber-100";
    case "H":
      return "bg-red-600 text-white";
    case "F":
      return "bg-slate-500 text-white";
    default:
      return "bg-gray-100 dark:bg-slate-800 dark:text-slate-200";
  }
};

// retourne un id de combat "unique" pour une paire (ordre indifférent)
const pairKey = (id1, id2) => {
  const a = Number(id1);
  const b = Number(id2);
  return a < b ? `${a}_${b}` : `${b}_${a}`;
};

// Convertit string JSON ou array d'ids en array d'ids normalisés (number si possible)
const safeParseIds = (val) => {
  if (Array.isArray(val)) return val.map((v) => Number(v) || v);
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p.map((v) => Number(v) || v) : [];
    } catch {
      return [];
    }
  }
  return [];
};

/* ------------------------------ Component ------------------------------ */

const RepartitionPouleTrois = ({
  competiteurs,
  distributionId,
  refreshDistribution,
  competitionId,
  tapisId,
  distributionCompetiteurs,
  genre, // 👈 new
  categorie_age, // 👈 new
  categorie_poids, // 👈 new
  discipline,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCombat, setSelectedCombat] = useState(null);
  const [positions, setPositions] = useState({
    position1: null,
    position2: null,
  });

  const [combats, setCombats] = useState([]);
  const [classement, setClassement] = useState([]);
  const [classementVisible, setClassementVisible] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const printRef = useRef();

  const capitalizeFr = (str = "") => {
    const s = String(str).toLocaleLowerCase("fr-FR");
    if (!s) return "";
    return s.charAt(0).toLocaleUpperCase("fr-FR") + s.slice(1);
  };

  // ordre des compétiteurs
  const orderedCompetiteurs = useMemo(() => {
    const base = Array.isArray(competiteurs) ? [...competiteurs] : [];

    // 1) Si on reçoit l'ordre exact via distributionCompetiteurs => on le respecte strictement
    const orderIds = safeParseIds(distributionCompetiteurs);
    if (orderIds.length > 0) {
      const idx = new Map(orderIds.map((id, i) => [Number(id) || id, i]));
      // on trie en se basant sur l'index trouvé (si non trouvé, on pousse à la fin)
      return base
        .map((c) => ({ ...c, __idx: idx.get(Number(c.id) || c.id) ?? 9999 }))
        .sort((a, b) => a.__idx - b.__idx);
    }

    // 2) Fallback : on lit une "position" si disponible (compat rétro), sinon ordre d'entrée
    return base
      .map((c, i) => ({ ...c, __pos: readPos(c, i) }))
      .sort((a, b) => a.__pos - b.__pos);
  }, [competiteurs, distributionCompetiteurs]);

  // Paires prévues pour une poule de 3 (sur l’ordre trié)
  const schedule = useMemo(() => {
    if (orderedCompetiteurs.length < 3) return [];
    return [
      [orderedCompetiteurs[0], orderedCompetiteurs[1], 1, 2],
      [orderedCompetiteurs[1], orderedCompetiteurs[2], 2, 3],
      [orderedCompetiteurs[0], orderedCompetiteurs[2], 1, 3],
    ];
  }, [orderedCompetiteurs]);

  // total combats théoriques poule de 3
  const MAX_COMBATS = 3;

  // charge les combats
  const fetchCombats = async () => {
    try {
      const fetched = await getDistributionCombats(distributionId);
      setCombats(fetched || []);
      updateClassement(fetched || []);
    } catch (e) {
      console.error("Erreur lors de la récupération des combats :", e);
    }
  };

  useEffect(() => {
    fetchCombats();
  }, [distributionId]);

  const refreshPouleTable = () => fetchCombats();

  // classement (victoires/points/disq/ex-aequo)
  const updateClassement = (fetchedCombats) => {
    const c = calculateClassementPoule(orderedCompetiteurs, fetchedCombats);
    setClassement(c);
  };

  // pour retrouver un combat (quel que soit l’ordre)
  const getCombatResult = (id1, id2) =>
    combats.find(
      (combat) =>
        (Number(combat.competiteur_id1) === Number(id1) &&
          Number(combat.competiteur_id2) === Number(id2)) ||
        (Number(combat.competiteur_id1) === Number(id2) &&
          Number(combat.competiteur_id2) === Number(id1)),
    );

  // rendu cellule matrice
  const renderCell = (c1, c2, r, c) => {
    if (c1.id === c2.id) {
      return (
        <td
          key={`${r}-${c}`}
          className="border bg-slate-600 dark:bg-slate-600 aspect-square w-16 h-16"
        />
      );
    }

    const combat = getCombatResult(c1.id, c2.id);

    if (!combat) {
      // Optionnel: petit hint quand aucun combat saisi
      return (
        <td
          key={`${r}-${c}`}
          className="border px-2 py-2 text-center text-sm"
          title="Cliquez sur le bouton 1×2 / 2×3 / 1×3 pour saisir le combat"
        />
      );
    }

    const isLeft = Number(combat.competiteur_id1) === Number(c1.id);
    const res = isLeft
      ? combat.resultat_competiteur1
      : combat.resultat_competiteur2;
    const pts = isLeft
      ? combat.points_competiteur1
      : combat.points_competiteur2;

    // 🆕 Tooltip : on privilégie details_combat si présent
    const tooltip =
      (combat.details_combat && String(combat.details_combat).trim()) ||
      `Résultat: ${res ?? "-"} · Points: ${pts ?? 0}`;

    return (
      <td
        key={`${r}-${c}`}
        className={`border px-1 py-1 text-center text-sm font-semibold rounded text-gray-900 dark:text-gray-900 ${resultColor(
          res,
        )}`}
        title={tooltip}
      >
        <div className="leading-tight select-none">
          <div className="text-base">{res || "-"}</div>
          <div className="text-[10px] opacity-80">( {pts ?? 0} )</div>
        </div>
      </td>
    );
  };

  const isCombatDone = (id1, id2) => !!getCombatResult(id1, id2);

  // ouverture modales (saisie/édition)
  const openModal = (competiteur1, competiteur2, position1, position2) => {
    const existing = getCombatResult(competiteur1.id, competiteur2.id);
    const initial = existing ? existing : { competiteur1, competiteur2 };
    setSelectedCombat(initial);
    setPositions({ position1, position2 });
    setIsModalOpen(true);
  };

  const openEditModal = (combat, position1, position2) => {
    const c1 = orderedCompetiteurs.find(
      (c) => Number(c.id) === Number(combat.competiteur_id1),
    );
    const c2 = orderedCompetiteurs.find(
      (c) => Number(c.id) === Number(combat.competiteur_id2),
    );
    setSelectedCombat({ ...combat, competiteur1: c1, competiteur2: c2 });
    setPositions({ position1, position2 });
    setIsEditModalOpen(true);
  };

  const handleCombatButtonClick = (c1, c2, p1, p2) => {
    const existing = getCombatResult(c1.id, c2.id);
    if (existing) openEditModal(existing, p1, p2);
    else openModal(c1, c2, p1, p2);
  };

  const handleModalSubmit = async () => {
    await refreshPouleTable();
  };

  // finalisation
  const confirmAndFinalize = async () => {
    const ok = window.confirm(
      "⚠️ Finaliser la distribution ?\nCette action est irréversible et figera le classement.",
    );
    if (!ok) return;
    await handleFinalizeDistribution();
  };

  const handleFinalizeDistribution = async () => {
    await updateAllCompetiteursResults();
    const resp = await finalizeDistribution(distributionId);
    if (resp) {
      alert("Distribution finalisée avec succès !");
      setIsFinalized(true);
      refreshDistribution?.();
    }
  };

  // push classement sur chaque compétiteur
  const updateAllCompetiteursResults = async () => {
    for (const c of classement) {
      if (!c?.id) continue;

      const payload = {
        classement_position: c.position || null,
        total_victoires: c.victoires || 0,
        total_points: c.points || 0,
        is_disqualified: !!c.disqualifie, // vraie exclusion compétition seulement
      };

      try {
        await updateCompetiteurResults(c.id, payload);
      } catch (e) {
        console.error("Update competiteur error", c.id, e);
      }
    }
  };

  const handleExportToPDF = async () => {
    const input = printRef.current;
    if (!input) return;

    try {
      // ---- Création du wrapper ----
      const wrapper = document.createElement("div");
      wrapper.style.background = "#ffffff";
      wrapper.style.color = "#000000";
      wrapper.style.padding = "36pt";
      wrapper.style.fontFamily = "Arial, sans-serif";
      wrapper.style.fontSize = "12px";
      wrapper.style.width = "1122pt";

      // ---- En-tête ----
      const header = document.createElement("div");
      header.style.display = "flex";
      header.style.justifyContent = "space-between";
      header.style.alignItems = "flex-start";
      header.style.marginBottom = "14pt";
      header.innerHTML = `
      <div>
        <div style="font-size:16pt;font-weight:800;margin-bottom:2pt;">Poule de 3</div>
        <div style="font-size:10pt;color:#333;">
          Distribution n° <strong>${distributionId}</strong>
        </div>
      </div>
      <div style="text-align:right;font-size:10pt;color:#333;">
        <div><strong>Sexe :</strong> ${(genre || "—").toUpperCase()}</div>
        <div><strong>Cat. âge :</strong> ${categorie_age || "—"}</div>
        <div><strong>Cat. poids :</strong> ${categorie_poids || "—"}</div>
      </div>
    `;
      wrapper.appendChild(header);

      // ---- Tableau principal cloné ----
      const clone = input.cloneNode(true);

      // 🆕 SI AUCUN COMBAT SAISI → vider V / Pts / Classement dans le CLONE
      if (!combats || combats.length === 0) {
        const bodyRows = clone.querySelectorAll("table.print-table tbody tr");

        bodyRows.forEach((tr) => {
          const cells = tr.querySelectorAll("td");
          // on s'assure qu'il y a bien assez de colonnes
          if (cells.length >= 3) {
            // colonnes V, Pts, Classement = les 3 dernières
            cells[cells.length - 3].textContent = "";
            cells[cells.length - 2].textContent = "";
            cells[cells.length - 1].textContent = "";
          }
        });
      }

      wrapper.appendChild(clone);

      // ---- Tableau détails_combat ----
      const rows = Array.isArray(combats) ? [...combats] : [];
      rows.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0)); // dernier en bas

      const detailsTitle = document.createElement("div");
      detailsTitle.textContent = "Détails des combats";
      detailsTitle.style.fontWeight = "800";
      detailsTitle.style.fontSize = "12pt";
      detailsTitle.style.margin = "24pt 0 6pt 0";
      wrapper.appendChild(detailsTitle);

      const detailsTable = document.createElement("table");
      detailsTable.style.width = "100%";
      detailsTable.style.borderCollapse = "collapse";
      detailsTable.innerHTML = `
      <thead>
        <tr style="background:#f3f4f6;">
          <th style="text-align:left;border:1px solid #e5e7eb;padding:6pt 8pt;width:60pt;">ID</th>
          <th style="text-align:left;border:1px solid #e5e7eb;padding:6pt 8pt;">Détails du combat</th>
        </tr>
      </thead>
      <tbody>
        ${
          rows.length
            ? rows
                .map((m) => {
                  const id = m?.id ?? "—";
                  const det =
                    (m?.details_combat && String(m.details_combat)) || "—";
                  const detHL = det.replace(
                    /^Gagnant\s*:\s*([^•|]+)/,
                    (_m, p1) =>
                      `<span style="background:#dcfce7;border:1px solid #86efac;padding:0 4pt;border-radius:4pt;"><strong>Gagnant : ${p1.trim()}</strong></span>`,
                  );
                  return `
                    <tr>
                      <td style="border:1px solid #e5e7eb;padding:6pt 8pt;font-weight:600;">${id}</td>
                      <td style="border:1px solid #e5e7eb;padding:6pt 8pt;">
                        <div style="white-space:pre-wrap;line-height:1.35">${detHL}</div>
                      </td>
                    </tr>
                  `;
                })
                .join("")
            : `<tr><td colspan="2" style="border:1px solid #e5e7eb;padding:10pt 8pt;color:#6b7280;text-align:center;">Aucun combat saisi pour l’instant.</td></tr>`
        }
      </tbody>
    `;
      wrapper.appendChild(detailsTable);

      // ---- Placement temporaire ----
      const staging = document.createElement("div");
      staging.style.position = "fixed";
      staging.style.left = "-200vw";
      staging.appendChild(wrapper);
      document.body.appendChild(staging);

      // ---- Capture avec nettoyage oklch ----
      const canvas = await html2canvas(wrapper, {
        backgroundColor: "#ffffff",
        useCORS: true,
        scale: 1,
        logging: false,
        removeContainer: true,
        onclone: (doc) => {
          doc.querySelectorAll("*").forEach((el) => {
            const cs = window.getComputedStyle(el);
            if (cs.backgroundColor.includes("oklch"))
              el.style.backgroundColor = "#ffffff";
            if (cs.color.includes("oklch")) el.style.color = "#000000";
          });
        },
      });

      document.body.removeChild(staging);

      // ---- Export PDF ----
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let y = 0;
      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      } else {
        while (y < imgHeight) {
          pdf.addImage(imgData, "PNG", 0, -y, imgWidth, imgHeight);
          y += pageHeight;
          if (y < imgHeight) pdf.addPage();
        }
      }

      pdf.save(`poule-3-distribution-${distributionId}.pdf`);
    } catch (err) {
      console.error("Erreur export PDF :", err);
    }
  };

  // Stats progression
  const uniqueCombatsDone = useMemo(() => {
    const setPairs = new Set();
    for (const m of combats)
      setPairs.add(pairKey(m.competiteur_id1, m.competiteur_id2));
    return setPairs.size;
  }, [combats]);
  const remaining = Math.max(0, MAX_COMBATS - uniqueCombatsDone);
  const percent = Math.round((uniqueCombatsDone / MAX_COMBATS) * 100);

  if (isFinalized) return null;

  /* ------------------------------ Render ------------------------------ */

  return (
    <div className="max-w-[1280px] mx-auto">
      {/* Header + actions */}
      <div className="mb-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-12 w-12 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-extrabold">
            #{distributionId}
          </div>
          <div>
            <h2 className="text-xl font-bold leading-tight">Poule de 3</h2>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Combats effectués :{" "}
              <span className="font-semibold">{uniqueCombatsDone}</span> /{" "}
              {MAX_COMBATS}
            </div>
          </div>
        </div>
      </div>

      {/* Progression */}
      <div className="mb-4 rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900 p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Progression des combats
          </div>
          <div className="text-sm">
            Restants : <span className="font-semibold">{remaining}</span>
          </div>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden">
          <div
            className={`h-2 ${
              percent >= 66
                ? "bg-emerald-500"
                : percent >= 33
                  ? "bg-amber-500"
                  : "bg-red-500"
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {percent}%
        </div>
      </div>

      {/* Tableau + légende (zone imprimable) */}
      <div className="w-full overflow-auto rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900">
        <div
          ref={printRef}
          className="print-container"
          style={{
            backgroundColor: "#fff",
            color: "#000",
            fontSize: "12px",
            fontFamily: "Arial, sans-serif",
            minWidth: 880,
            padding: 16, // un peu de marge interne
          }}
        >
          <table className="print-table table-auto border-collapse w-full mb-3 font-roboto">
            <thead>
              <tr className="bg-gray-100 text-gray-800 text-sm">
                <th className="border px-2 py-2">N°</th>
                <th className="border px-2 py-2">Compétiteurs</th>
                <th className="border px-2 py-2">Clubs</th>
                <th className="border px-2 py-2">Poids</th>
                <th className="border px-2 py-2">Ceinture</th>
                <th className="border px-2 py-2">1</th>
                <th className="border px-2 py-2">2</th>
                <th className="border px-2 py-2">3</th>
                <th className="border px-2 py-2">V</th>
                <th className="border px-2 py-2">Pts</th>
                <th className="border px-2 py-2">Class.</th>
              </tr>
            </thead>
            <tbody>
              {orderedCompetiteurs.map((c, ri) => {
                const rowClassement = classement.find(
                  (x) => Number(x.id) === Number(c.id),
                );
                const victories = rowClassement?.victoires ?? 0;
                const points = rowClassement?.points ?? 0;
                const isDQ = !!rowClassement?.disqualifie;

                let classementCell = "";
                if (classementVisible) {
                  if (isDQ) classementCell = "Disq.";
                  else
                    classementCell = rowClassement?.exAequo
                      ? `${rowClassement?.position ?? ""} (ex aequo)`
                      : (rowClassement?.position ?? "");
                }

                return (
                  <tr key={c.id} className="odd:bg-white even:bg-gray-50">
                    <td className="border px-2 py-2 text-center text-sm font-semibold w-8">
                      {ri + 1}
                    </td>
                    <td className="border px-2 py-2 text-left font-semibold">
                      {String(c.nom_competiteur || "").toLocaleUpperCase(
                        "fr-FR",
                      )}{" "}
                      <span className="font-normal">
                        {capitalizeFr(c.prenom_competiteur)}
                      </span>
                    </td>

                    <td className="border px-2 py-2 text-left text-xs">
                      {c.club_competiteur}
                    </td>
                    <td className="border px-2 py-2 text-center text-xs">
                      {c.poids_competiteur ?? "—"}
                    </td>
                    <td className="border px-2 py-2 text-center text-xs">
                      {c.grade_competiteur || "—"}
                    </td>

                    {orderedCompetiteurs.map((opp, ci) =>
                      renderCell(c, opp, ri, ci),
                    )}

                    <td className="border px-2 py-2 text-center text-sm font-semibold">
                      {victories}
                    </td>
                    <td className="border px-2 py-2 text-center text-sm font-semibold">
                      {points}
                    </td>
                    <td
                      className={`border px-2 py-2 text-center text-sm font-semibold ${
                        isDQ ? "bg-gray-300" : ""
                      }`}
                    >
                      {classementCell}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Légende résultats */}
          <div className="mb-4 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-5 rounded bg-emerald-300" /> Victoire (V)
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-5 rounded bg-rose-300" /> Défaite (D)
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-5 rounded bg-amber-300" /> Nul (N)
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-5 rounded bg-red-600" /> Hansoku Make(H)
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-5 rounded bg-slate-500" /> Forfait (F)
            </span>
          </div>

          {/* Boutons combats rapides */}
          {schedule.length === 3 && (
            <div className="mt-2 mb-2 flex flex-wrap items-center gap-2">
              {schedule.map(([a, b, p1, p2], idx) => {
                const done = isCombatDone(a.id, b.id);
                return (
                  <button
                    key={idx}
                    className={`px-4 py-2 rounded text-white ${
                      done ? "bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => handleCombatButtonClick(a, b, p1, p2)}
                    title={`${a.nom_competiteur} vs ${b.nom_competiteur}`}
                  >
                    {p1}×{p2}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Action bar collante (mobile friendly) */}
      <div className="sticky bottom-2 mt-3 flex flex-wrap gap-2 justify-end">
        <div className="inline-flex gap-2 rounded-xl bg-white/90 dark:bg-slate-900/90 backdrop-blur border border-gray-200 dark:border-slate-700 p-2 shadow">
          <button
            onClick={() => setIsDetailsOpen(true)}
            className="rounded-lg bg-slate-700 text-white px-3 py-2 hover:bg-slate-800"
            title="Voir la liste des combats et leurs détails"
          >
            Détails des combats
          </button>
          <button
            onClick={refreshPouleTable}
            className="rounded-lg bg-blue-600 text-white px-3 py-2 hover:bg-blue-700"
          >
            Recharger
          </button>
          <button
            onClick={() => setClassementVisible((v) => !v)}
            className="rounded-lg bg-emerald-600 text-white px-3 py-2 hover:bg-emerald-700"
          >
            {classementVisible ? "Masquer Classement" : "Afficher Classement"}
          </button>
          <button
            onClick={handleExportToPDF}
            className="rounded-lg bg-slate-800 text-white px-3 py-2 hover:bg-slate-900"
            title="Exporter en PDF"
          >
            📄 Exporter PDF
          </button>
          <button
            onClick={confirmAndFinalize}
            className="rounded-lg bg-rose-600 text-white px-3 py-2 hover:bg-rose-700"
          >
            Finaliser
          </button>
        </div>
      </div>

      {/* Modales */}
      <CombatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        competiteur1={selectedCombat?.competiteur1}
        competiteur2={selectedCombat?.competiteur2}
        onSubmit={handleModalSubmit}
        initialData={selectedCombat || {}}
        distributionId={distributionId}
        position1={positions.position1}
        position2={positions.position2}
        refreshPouleTable={refreshPouleTable}
        competitionId={competitionId}
        tapisId={tapisId}
        discipline={discipline} // ✅ ajout
      />
      <EditCombatModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        competiteur1={selectedCombat?.competiteur1}
        competiteur2={selectedCombat?.competiteur2}
        initialData={selectedCombat}
        distributionId={distributionId}
        position1={positions.position1}
        position2={positions.position2}
        refreshPouleTable={refreshPouleTable}
        discipline={discipline}
      />

      {/* ---- AJOUT : modal détails ---- */}
      <CombatDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        combats={combats}
        onChanged={() => fetchCombats()}
      />
    </div>
  );
};

export default RepartitionPouleTrois;
